import React, { memo, useRef, useState, useLayoutEffect } from 'react'
import AnchorLink from 'react-anchor-link-smooth-scroll'
import Font from 'react-font'

const Navbar: React.FC = memo(() => {
  const ref = useRef<HTMLElement>(null)
  const [height, setHeight] = useState<number>(0)
  const [isOpen, setIsOpen] = useState<boolean>(false)
  useLayoutEffect(() => {
    const node = ref.current
    if (!node) return
    // ResizeObserver (rather than a one-off measurement) keeps `height`
    // correct once the logo image finishes loading and the nav grows taller
    // than its initial text-only height — a plain offsetHeight read here
    // races the async image load and undercounts the offset used by the
    // anchor links below.
    const observer = new ResizeObserver(([entry]) => {
      setHeight(entry.borderBoxSize?.[0]?.blockSize ?? node.offsetHeight)
    })
    observer.observe(node)
    return () => observer.disconnect()
  }, [])
  return (
    <nav
      className="flex items-center justify-between flex-wrap bg-brand-cream sticky top-0 z-50 w-full shadow-sm px-4 md:px-8 py-2"
      ref={ref}
    >
      <AnchorLink href="#top" offset="2000">
        <div className="flex items-center flex-shrink-0 text-white mr-6">
          <img
            src="logo.svg"
            alt="Mariana Castelo - Nutricionista"
            className="h-12 w-auto md:h-auto"
          />
        </div>
      </AnchorLink>

      <div className="block lg:hidden">
        <button
          type="button"
          aria-label="Abrir menu"
          aria-expanded={isOpen}
          onClick={() => setIsOpen((open) => !open)}
          className="flex items-center px-3 py-2 border rounded text-brand-dark border-brand-dark hover:text-brand-taupe hover:border-brand-taupe"
        >
          <svg
            className="fill-current h-3 w-3"
            viewBox="0 0 20 20"
            xmlns="http://www.w3.org/2000/svg"
          >
            <title>Menu</title>
            <path d="M0 3h20v2H0V3zm0 6h20v2H0V9zm0 6h20v2H0v-2z" />
          </svg>
        </button>
      </div>
      <div
        className={`w-full flex-grow lg:flex lg:items-center lg:w-auto ${
          isOpen ? 'block' : 'hidden lg:block'
        }`}
      >
        <div className="flex flex-col gap-1 mt-4 border-t border-brand-sand pt-4 text-sm lg:flex-row lg:gap-0 lg:mt-0 lg:border-t-0 lg:pt-0 lg:flex-grow">
          {[
            ['Sobre', '#about'],
            ['Serviços', '#services'],
            ['Agendamento', '#scheduling'],
          ].map(([title, url]) => (
            <AnchorLink
              key={url}
              offset={() => height + 4}
              href={url}
              onClick={() => setIsOpen(false)}
              className="block rounded-lg px-3 py-2 lg:inline-block lg:px-0 lg:py-0 font-bold text-lg text-brand-dark hover:text-brand-taupe hover:bg-brand-sand lg:hover:bg-transparent lg:mr-4"
            >
              <Font family="Nunito" text={title}>
                <>{title}</>
              </Font>
            </AnchorLink>
          ))}
        </div>
      </div>
    </nav>
  )
})
Navbar.displayName = 'Navbar'

export default Navbar
